import React, { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/authContext";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Clock,
  CalendarClock,
  CalendarDays,
  Send,
  CheckSquare,
  Palmtree,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Briefcase,
  Users,
  CreditCard,
  Layers,
  FileText,
  PieChart,
  Tags,
  Users2,
  Box,
  Download,
  LayoutTemplate,
  LogOut,
  Landmark,
  BookUser,
  Trash2,
  Building2,
  Shield,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { PREF_SIDEBAR_AUTO_COLLAPSE } from "@/lib/userPrefs";
import { ThemeSwitcher } from "@/components/custom/ThemeSwitcher";

export function AppSidebar() {
  const [location] = useLocation();
  const { user, signOut } = useAuth();
  const role = user?.role || "staff";
  // Initialize collapsed state from localStorage preference.
  // Each page has its own Layout (AppSidebar remounts on navigation), so the
  // initializer — not a useEffect — is the reliable mechanism for auto-collapse.
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(PREF_SIDEBAR_AUTO_COLLAPSE);
      return raw === null ? true : JSON.parse(raw) === true;
    } catch {
      return true;
    }
  });
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({});
  // Also collapse on in-session navigation (covers cases where Layout persists).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PREF_SIDEBAR_AUTO_COLLAPSE);
      const autoCollapse = raw === null ? true : JSON.parse(raw) === true;
      if (autoCollapse) setIsCollapsed(true);
    } catch {
      setIsCollapsed(true);
    }
  }, [location]);

  interface NavItem {
    label: string;
    href: string;
    icon: React.ElementType;
    roles?: string[];
    onClick?: () => void;
    badge?: number;
  }
  interface NavModule {
    id: string;
    label: string;
    icon: React.ElementType;
    roles?: string[];
    items: NavItem[];
  }

  const toggleModule = (id: string) => {
    if (isCollapsed) {
        setIsCollapsed(false);
        setOpenModules(prev => ({ ...prev, [id]: true }));
    } else {
        setOpenModules(prev => ({ ...prev, [id]: !prev[id] }));
    }
  };

  const navModules: NavModule[] = [
    {
      id: "workflow",
      label: "Workflow",
      icon: Layers,
      roles: ["staff", "manager", "admin", "contractor"],
      items: [
        { label: "Projects", href: "/", icon: Briefcase },
        { label: "Tasks", href: "/tasks", icon: CheckSquare },
        { label: "Templates", href: "/templates", icon: LayoutTemplate },
      ]
    },
    {
      id: "clients",
      label: "Clients",
      icon: Users,
      roles: ["staff", "manager", "admin", "contractor"],
      items: [
        { label: "My Accounts", href: "/my-accounts", icon: BookUser, roles: ["staff", "manager", "admin"] },
        { label: "Accounts", href: "/accounts", icon: Briefcase },
        { label: "Client Groups", href: "/client-groups", icon: Layers },
        { label: "Financial Sources", href: "/sources", icon: Landmark, roles: ["staff", "manager", "admin"] },
        { label: "Contacts", href: "/contacts", icon: Users2 },
        { label: "Engagements", href: "/engagements", icon: Box },
      ]
    },
    {
      id: "time",
      label: "Time",
      icon: Clock,
      roles: ["staff", "manager", "admin", "contractor"],
      items: [
        { label: "Today", href: "/today", icon: Clock },
        { label: "Calendar", href: "/calendar", icon: CalendarDays, roles: ["staff", "manager", "admin", "contractor"] },
        { label: "Scheduling", href: "/scheduling", icon: CalendarClock, roles: ["staff", "manager", "admin"] },
        { label: "Timesheet", href: "/period", icon: CalendarClock },
        { label: "Submit", href: "/submit", icon: Send },
        { label: "Approvals", href: "/review", icon: CheckSquare, roles: ["manager", "admin"] },
        { label: "All Timesheets", href: "/timesheets/all", icon: Users, roles: ["manager", "admin"] },
        { label: "Pay Report", href: "/reports?tab=payroll", icon: PieChart, roles: ["manager", "admin"] },
        { label: "PTO", href: "/pto", icon: Palmtree },
      ]
    },
    {
      id: "reports",
      label: "Reports",
      icon: BarChart3,
      roles: ["manager", "admin"],
      items: [
        { label: "Utilization", href: "/reports", icon: PieChart },
        { label: "Time Report", href: "/reports/time", icon: BarChart3 },
        { label: "Capacity", href: "/reports/capacity", icon: Palmtree },
      ]
    },
    {
      id: "billing",
      label: "Billing",
      icon: CreditCard,
      roles: ["manager", "admin"],
      items: [
        { label: "Invoices", href: "/invoices", icon: FileText },
        { label: "Exports", href: "/exports", icon: Download },
      ]
    },
    {
      id: "settings",
      label: "Settings",
      icon: Settings,
      roles: ["staff", "manager", "admin", "contractor"],
      items: [
        { label: "General", href: "/settings", icon: Settings },
        { label: "Tags", href: "/tags", icon: Tags },
        { label: "Admin", href: "/settings/admin", icon: Shield, roles: ["admin"] },
      ]
    },
  ];

  return (
    <div 
      className={cn(
        "flex flex-col h-full bg-sidebar text-sidebar-foreground border-r border-sidebar-border shadow-xl transition-all duration-300 relative",
        isCollapsed ? "w-20" : "w-64"
      )}
    >
      <button 
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-20 bg-card border border-border text-foreground p-1 rounded-full shadow-md z-50 hover:bg-accent transition-colors"
        data-testid="button-toggle-sidebar"
      >
        {isCollapsed ? <PanelLeftOpen className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
      </button>

      <div
        className={cn("p-4 border-b border-sidebar-border/50 flex flex-col items-center transition-all duration-300", isCollapsed ? "px-2" : "px-6")}
        onClick={isCollapsed ? () => setIsCollapsed(false) : undefined}
        style={isCollapsed ? { cursor: "pointer" } : undefined}
        title={isCollapsed ? "Expand sidebar" : undefined}
      >
        <div className={cn(
            "bg-white rounded-xl shadow-md border border-white/10 flex justify-center transition-all duration-300",
            isCollapsed ? "w-12 h-12 p-1 rounded-lg" : "w-full px-4 py-4"
        )}>
           <img
            src="/logo.png"
            alt="Carpenter & Company"
            className={cn("object-contain transition-all", isCollapsed ? "h-full w-full" : "h-16 w-auto")}
           />
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-2 overflow-y-auto scrollbar-thin scrollbar-thumb-sidebar-accent scrollbar-track-transparent">
        {navModules.map((module) => {
            const hasAccess = !module.roles || module.roles.includes(role);
            if (!hasAccess) return null;

            const isOpen = openModules[module.id];
            const visibleItems = module.items.filter(item => !item.roles || item.roles.includes(role));
            const isChildActive = visibleItems.some(item => location === item.href);

            if (isCollapsed) {
                return (
                    <TooltipProvider key={module.id} delayDuration={0}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    onClick={() => toggleModule(module.id)}
                                    className={cn(
                                        "w-full flex items-center justify-center p-3 rounded-lg transition-all duration-200 group",
                                        isChildActive ? "bg-sidebar-primary text-sidebar-primary-foreground" : "hover:bg-sidebar-accent text-sidebar-foreground/70 hover:text-sidebar-accent-foreground"
                                    )}
                                    data-testid={`nav-module-${module.id}`}
                                    aria-label={module.label}
                                >
                                    <module.icon className="w-5 h-5" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent side="right" className="bg-popover text-popover-foreground border-border font-medium ml-2">
                                <p>{module.label}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                );
            }

            return (
                <div key={module.id} className="space-y-1">
                    <button 
                        onClick={() => toggleModule(module.id)}
                        className={cn(
                            "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200 group select-none",
                            isChildActive && !isOpen 
                                ? "bg-sidebar-accent/50 text-sidebar-foreground border-l-4 border-sidebar-primary" 
                                : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        )}
                        data-testid={`nav-module-${module.id}`}
                    >
                        <div className="flex items-center gap-3">
                            <module.icon className="w-4.5 h-4.5 text-sidebar-primary" />
                            <span>{module.label}</span>
                        </div>
                        {isOpen ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
                    </button>

                    {isOpen && (
                        <div className="space-y-0.5 ml-3 pl-3 border-l border-sidebar-border/30 animate-in slide-in-from-top-2 duration-200">
                            {visibleItems.map(item => {
                                // Detail routes (/accounts/:id etc.) highlight their parent item
                                const detailPrefixes = ["/accounts", "/templates", "/invoices"];
                                const isActive = !item.onClick && (location === item.href ||
                                    (detailPrefixes.includes(item.href) && location.startsWith(item.href + "/")));
                                const itemClass = cn(
                                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer w-full text-left",
                                    isActive
                                        ? "bg-sidebar-primary text-sidebar-primary-foreground font-medium shadow-sm"
                                        : "text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
                                );
                                const testId = `nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`;
                                if (item.onClick) {
                                    return (
                                        <button key={item.label} onClick={item.onClick} className={itemClass} data-testid={testId}>
                                            <span>{item.label}</span>
                                        </button>
                                    );
                                }
                                return (
                                    <Link key={item.href} href={item.href}>
                                        <div className={itemClass} data-testid={testId}>
                                            <span className="flex-1">{item.label}</span>
                                            {item.badge !== undefined && item.badge > 0 && (
                                                <span className="ml-1 bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">
                                                    {item.badge}
                                                </span>
                                            )}
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        })}
      </nav>

      {/* Trash link */}
      <div className={cn("px-3 pb-1", isCollapsed ? "flex justify-center" : "")}>
        <Link href="/trash" className={cn(
            "flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors",
            isCollapsed ? "justify-center" : "w-full",
            location === "/trash" ? "bg-sidebar-accent/50 text-sidebar-foreground" : ""
          )}>
            <Trash2 className="w-4 h-4 shrink-0 text-sidebar-primary" />
            {!isCollapsed && <span>Trash</span>}
          </Link>
      </div>

      <div
        className="px-3 py-4 border-t border-sidebar-border/50 bg-black/10 space-y-1"
        onClick={isCollapsed ? () => setIsCollapsed(false) : undefined}
        style={isCollapsed ? { cursor: "pointer" } : undefined}
        title={isCollapsed ? "Expand sidebar" : undefined}
      >
        <div className={cn("flex items-center gap-3 pb-3 transition-all", isCollapsed ? "justify-center" : "px-3")}>
           <Avatar className="h-9 w-9 border-2 border-sidebar-primary/20 shadow-sm cursor-pointer hover:border-sidebar-primary transition-colors">
              <AvatarFallback className="bg-sidebar-primary text-sidebar-primary-foreground text-xs font-bold">
                {user?.avatar || "U"}
              </AvatarFallback>
            </Avatar>
            {!isCollapsed && (
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate leading-tight">{user?.name || "User"}</p>
                    <p className="text-xs text-sidebar-foreground/50 truncate mt-0.5">{user?.email || ""}</p>
                </div>
            )}
        </div>

        <div
          className={cn("flex", isCollapsed ? "w-fit mx-auto" : "")}
          onClick={isCollapsed ? (e) => e.stopPropagation() : undefined}
        >
          <ThemeSwitcher isCollapsed={isCollapsed} />
        </div>

        {isCollapsed ? (
          <div className="flex justify-center">
            <button
              type="button"
              onClick={e => { e.stopPropagation(); signOut(); }}
              title="Sign out"
              data-testid="button-signout"
              className="p-2 rounded-md text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 transition-colors"
            >
              <LogOut className="w-4 h-4 text-sidebar-primary" />
            </button>
          </div>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start gap-3 px-3 py-2 text-sm text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/50"
              onClick={signOut}
              data-testid="button-signout"
            >
              <LogOut className="w-4 h-4 text-sidebar-primary" />
              Sign Out
            </Button>
            <p className="px-3 pt-1 text-right text-xs text-sidebar-foreground/50 font-mono">v{__APP_VERSION__}</p>
          </>
        )}
      </div>

    </div>
  );
}
